import React, { useState, useRef } from "react";
import "./css/credit_report_upload.scss";

import { useDocument } from "../../../components/loan_tools/loan_tool";
import { fromMismoXml } from "../../../mismo/mismo";
import { fromCreditResponseXml } from "../../../mismo/credit_response";
import CurrencyField from "../../../components/fields/currency_field";
import generate_id from "../../../constants/id-generator";
import { ACCOUNT_ID_PREFIX } from "../../../constants/debt-info";
import { INCLUDE_IN_DTI } from "../../../constants/account_dispositions";
import {
  spouseIndex,
  borrowerFullName,
  householdLabel
} from "../../../constants/debt_owners";

/**
 * Upload a saved credit-report / MISMO 3.4 XML on the Debt page, preview the
 * tradelines it contains, and import the chosen ones into the Debt page.
 *
 * Uploading a SAVED report is also how an LO avoids paying the credit company
 * to re-issue a report they already pulled.
 *
 * Phase 1: parsing runs through the proven fromMismoXml import (DU / Fannie Mae
 * 3.4 <LIABILITY> format). The live pull and the MeridianLink-native
 * <CREDIT_LIABILITY> format are separate follow-ups.
 */
export default function CreditReportUpload() {
  const [document, setDocument, { documentType }, { saveDocument }] =
    useDocument();
  const fileRef = useRef();
  const [rows, setRows] = useState(null); // parsed tradelines awaiting review
  const [ignoreZeroBalance, setIgnoreZeroBalance] = useState(true);
  const [onlyWithPayment, setOnlyWithPayment] = useState(true);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState("");

  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  // Which borrower(s) the uploaded report is FOR. The LO picks this BEFORE
  // choosing the file so the tradelines are directed to the right borrower
  // section (privacy wall) - critical when there are unmarried borrowers and
  // separate reports. One person or one married couple at a time.
  // Credit scores parsed from the uploaded file (one entry per file borrower, in
  // file order), applied to the selected doc borrowers on import.
  const [fileBorrowers, setFileBorrowers] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickSel, setPickSel] = useState(() => {
    const init = { 0: true };
    const sp = spouseIndex(borrowers, 0);
    if (sp !== -1) init[sp] = true;
    return init;
  });
  // The confirmed pull-group indices, read inside the async file reader.
  const pickIndicesRef = useRef([0]);

  const pickIndices = Object.keys(pickSel)
    .filter(k => pickSel[k])
    .map(Number)
    .sort((a, b) => a - b);
  const pickOk =
    pickIndices.length === 1 ||
    (pickIndices.length === 2 &&
      spouseIndex(borrowers, pickIndices[0]) === pickIndices[1]);
  const pickJoint = pickIndices.length > 1;

  // Re-own every imported tradeline to the selected group: a joint line -> the
  // whole group, an individual line -> the group's primary borrower. This is
  // what "directs the report to the right borrower(s)" and keeps one borrower's
  // pulled credit off another's application.
  function remapOwners(account) {
    const idx = pickIndicesRef.current;
    const isJoint = (account.owners || []).length > 1;
    return {
      ...account,
      owners: isJoint ? idx.map(String) : [String(idx[0])]
    };
  }

  function num(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }

  // A tradeline passes the filters when it isn't excluded by the two toggles.
  function passesFilters(row) {
    if (ignoreZeroBalance && num(row.current_balance) <= 0) return false;
    if (onlyWithPayment && num(row.monthly_payment) <= 0) return false;
    return true;
  }

  function onPickFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError("");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        let accounts = [];
        let parsedBorrowers = [];
        // Try the DU / Fannie <LIABILITY> path first; a native MeridianLink
        // credit response (CREDIT_LIABILITY) lives in a different subtree, so
        // fall back to it whether the DU parse returns nothing OR throws.
        try {
          const { document: parsed } = fromMismoXml(reader.result, {
            documentType
          });
          accounts = Object.values(parsed.accountSummaries || {});
          // Capture the file's per-borrower credit scores (DU CREDIT_SCORES).
          parsedBorrowers =
            (parsed.applicant && parsed.applicant.borrowers) || [];
        } catch (duErr) {
          accounts = [];
          parsedBorrowers = [];
        }
        if (accounts.length === 0) {
          const { document: nativeParsed } = fromCreditResponseXml(
            reader.result,
            { ownerIndices: pickIndicesRef.current.map(String) }
          );
          accounts = Object.values(nativeParsed.accountSummaries || {});
        }
        if (accounts.length === 0) {
          setError(
            "No tradelines were found in that file. Confirm it is a MISMO 3.4 credit / liability XML."
          );
          return;
        }
        // Direct every tradeline to the borrower group the LO chose.
        accounts = accounts.map(remapOwners);
        setFileBorrowers(parsedBorrowers);
        // Pre-select everything that passes the default filters.
        const preselect = {};
        accounts.forEach((a, i) => {
          preselect[i] = passesFiltersWith(a, true, true);
        });
        setRows(accounts);
        setSelected(preselect);
      } catch (err) {
        setError(`Could not read that file: ${err.message || "unknown error"}`);
      }
    };
    reader.readAsText(file);
    // Allow re-picking the same file name later.
    e.target.value = "";
  }

  // Filter check with explicit toggle values (used at load before state settles).
  function passesFiltersWith(row, zeroBal, withPay) {
    if (zeroBal && num(row.current_balance) <= 0) return false;
    if (withPay && num(row.monthly_payment) <= 0) return false;
    return true;
  }

  // Re-run the pre-selection whenever a toggle changes, so the checkboxes track
  // the filters (the LO can still hand-pick after).
  function applyToggle(nextZero, nextPay) {
    setIgnoreZeroBalance(nextZero);
    setOnlyWithPayment(nextPay);
    if (!rows) return;
    const next = {};
    rows.forEach((a, i) => {
      next[i] = passesFiltersWith(a, nextZero, nextPay);
    });
    setSelected(next);
  }

  function ownerLabel(account) {
    const owners = account.owners && account.owners.length ? account.owners : [0];
    return owners
      .map(
        o =>
          borrowerFullName(borrowers[parseInt(o, 10)]) ||
          `Borrower ${parseInt(o, 10) + 1}`
      )
      .join(" + ");
  }

  function keyOf(account) {
    return `${String(account.account_number || "").trim()}|${String(
      account.description || ""
    )
      .trim()
      .toLowerCase()}`;
  }

  function importSelected() {
    const updated = { ...document.accountSummaries };
    const existingKeys = new Set(Object.values(updated).map(keyOf));
    const updatedLoans = document.loans.map(loan => ({
      ...loan,
      account_dispositions: { ...loan.account_dispositions }
    }));

    rows.forEach((account, i) => {
      if (!selected[i]) return;
      // Skip a tradeline already on the Debt page (e.g. re-uploading a report).
      const key = keyOf(account);
      if (account.account_number && existingKeys.has(key)) return;

      const id = generate_id(
        ACCOUNT_ID_PREFIX,
        true,
        Object.keys(updated).length
      );
      updated[id] = { ...account, id };
      existingKeys.add(key);
      updatedLoans.forEach(loan => {
        loan.account_dispositions[id] = INCLUDE_IN_DTI;
      });
    });

    // Apply the file's credit scores to the SELECTED borrower(s) only, mapped by
    // position (file borrower k -> the k-th selected doc borrower). Scores never
    // cross to a borrower who wasn't on this report (privacy wall). Only non-empty
    // score fields overwrite, so a scoreless file leaves existing scores alone.
    const idx = pickIndicesRef.current || [];
    const updatedBorrowers = document.applicant.borrowers.map(b => ({ ...b }));
    const SCORE_FIELDS = [
      "credit_score_experian",
      "credit_score_transunion",
      "credit_score_equifax",
      "credit_mid_score",
      "lowest_credit_mid_score"
    ];
    (fileBorrowers || []).forEach((fb, k) => {
      const target = idx[k];
      if (target == null || !updatedBorrowers[target] || !fb) return;
      SCORE_FIELDS.forEach(field => {
        if (fb[field] !== undefined && fb[field] !== "") {
          updatedBorrowers[target][field] = fb[field];
        }
      });
    });

    setDocument({
      ...document,
      accountSummaries: updated,
      loans: updatedLoans,
      applicant: { ...document.applicant, borrowers: updatedBorrowers }
    });
    saveDocument();
    setRows(null);
    setSelected({});
  }

  const selectedCount = rows
    ? rows.filter((_, i) => selected[i]).length
    : 0;

  return (
    <div className="credit-report-upload">
      <input
        ref={fileRef}
        type="file"
        accept=".xml,text/xml,application/xml"
        style={{ display: "none" }}
        onChange={onPickFile}
      />
      <button
        className="button"
        onClick={() => setShowPicker(true)}
        disabled={borrowers.length === 0}
      >
        Upload Credit Report (XML)
      </button>
      {error && <div className="credit-upload-error">{error}</div>}

      {showPicker && (
        <div className="credit-upload-overlay">
          <div className="credit-upload-modal cu-picker">
            <div className="credit-upload-modal-header">
              Which borrower is this report for?
            </div>
            <div className="cu-picker-body">
              <p className="cu-picker-note">
                Pick the borrower or married couple this saved / re-issued report
                belongs to — one at a time — so it imports onto the right
                borrower section only.
              </p>
              <div className="cu-picker-list">
                {borrowers.map((b, i) => {
                  const sp = spouseIndex(borrowers, i);
                  const on = !!pickSel[i];
                  return (
                    <label
                      key={i}
                      className={`cu-pick-row ${on ? "picked" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={e => {
                          // Capture BEFORE the functional updater: React 16 pools
                          // synthetic events, so reading e.target inside the
                          // deferred updater would throw (event already recycled).
                          const checked = e.target.checked;
                          setPickSel(prev => {
                            const next = { ...prev };
                            if (checked) next[i] = true;
                            else delete next[i];
                            return next;
                          });
                        }}
                      />
                      <span>
                        <span className="cu-pick-name">
                          {borrowerFullName(b) || `Borrower ${i + 1}`}
                          {sp !== -1 && (
                            <span className="cu-married-tag">
                              married to {borrowerFullName(borrowers[sp])}
                            </span>
                          )}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <div
                className={`cu-pick-banner ${
                  pickIndices.length === 0
                    ? "info"
                    : !pickOk
                    ? "warn"
                    : pickJoint
                    ? "ok"
                    : "accent"
                }`}
              >
                {pickIndices.length === 0
                  ? "Select the borrower(s) this report is for."
                  : !pickOk
                  ? "Pick one person, or one married couple."
                  : `Import onto: ${householdLabel(borrowers, pickIndices[0])}.`}
              </div>
              <div className="cu-picker-charge">
                A saved file on your PC is free to import. A bureau re-issue or a
                rapid rescore is a new, billable order — place those on{" "}
                <b>Request Credit Report</b>.
              </div>
            </div>
            <div className="credit-upload-actions">
              <div />
              <div>
                <button
                  className="button inverse-button"
                  onClick={() => setShowPicker(false)}
                >
                  Cancel
                </button>
                <button
                  className="button"
                  disabled={!pickOk}
                  onClick={() => {
                    pickIndicesRef.current = pickIndices;
                    setShowPicker(false);
                    fileRef.current.click();
                  }}
                >
                  Choose file…
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rows && (
        <div className="credit-upload-overlay">
          <div className="credit-upload-modal">
            <div className="credit-upload-modal-header">
              Review Credit Report Tradelines
              {pickIndicesRef.current && pickIndicesRef.current.length > 0 && (
                <span className="credit-upload-target">
                  {" "}
                  — importing onto{" "}
                  {householdLabel(borrowers, pickIndicesRef.current[0])}
                </span>
              )}
            </div>

            <div className="credit-upload-filters">
              <label>
                <input
                  type="checkbox"
                  checked={ignoreZeroBalance}
                  onChange={e =>
                    applyToggle(e.target.checked, onlyWithPayment)
                  }
                />{" "}
                Ignore zero balances
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={onlyWithPayment}
                  onChange={e =>
                    applyToggle(ignoreZeroBalance, e.target.checked)
                  }
                />{" "}
                Only lines with a monthly payment
              </label>
            </div>

            <div className="credit-upload-table-wrap">
              <table className="credit-upload-table">
                <thead>
                  <tr>
                    <th />
                    <th>Creditor</th>
                    <th>Type</th>
                    <th className="right">Balance</th>
                    <th className="right">Monthly</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((account, i) => (
                    <tr
                      key={i}
                      className={passesFilters(account) ? "" : "filtered-out"}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selected[i]}
                          onChange={e =>
                            setSelected({ ...selected, [i]: e.target.checked })
                          }
                        />
                      </td>
                      <td>{account.description || "—"}</td>
                      <td>{account.liability_type || "—"}</td>
                      <td className="right">
                        <CurrencyField
                          naked
                          fixedDigits={0}
                          value={account.current_balance || 0}
                        />
                      </td>
                      <td className="right">
                        <CurrencyField
                          naked
                          fixedDigits={0}
                          value={account.monthly_payment || 0}
                        />
                      </td>
                      <td>{ownerLabel(account)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="credit-upload-actions">
              <div className="credit-upload-count">
                {selectedCount} of {rows.length} selected
                {(fileBorrowers || []).some(
                  fb =>
                    fb &&
                    (fb.credit_score_experian ||
                      fb.credit_score_transunion ||
                      fb.credit_score_equifax ||
                      fb.credit_mid_score)
                ) && (
                  <span className="credit-upload-scores-note">
                    {" "}
                    · credit scores found — will update{" "}
                    {householdLabel(borrowers, (pickIndicesRef.current || [0])[0])}
                  </span>
                )}
              </div>
              <div>
                <button
                  className="button inverse-button"
                  onClick={() => {
                    setRows(null);
                    setSelected({});
                  }}
                >
                  Cancel
                </button>
                <button
                  className="button"
                  disabled={selectedCount === 0}
                  onClick={importSelected}
                >
                  Import to Debt Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
